import type { Ref } from 'vue'

import type { AnimationLayer } from '@/types/animationLayer'
import type { GradientStop } from '@/constants/gradientPresets'

/** 与 `mockupEditor.backgroundMode` 一致，避免桥接模块反向引用 store 造成循环依赖 */
export type MockupBackgroundModeId = 'color' | 'gradient' | 'transparent' | 'image'

/**
 * 历史命令对 `mockupEditor` 的可变操作面（由 store 在初始化时注入，避免命令文件依赖 Pinia 实例形态）。
 */
export type MockupHistoryBridge = {
  readonly animationLayers: Ref<AnimationLayer[]>
  readonly selectedLayerId: Ref<string | null>
  readonly selectedClipId: Ref<string | null>
  readonly currentTimeSeconds: Ref<number>
  readonly backgroundHex: Ref<string>
  readonly backgroundMode: Ref<MockupBackgroundModeId>
  readonly gradientStops: Ref<GradientStop[]>
  readonly activeGradientPresetId: Ref<string | null>
  /** 与 store 中 `timelineMaxSeconds` 计算一致 */
  getTimelineMaxSeconds(): number
  /** 片段几何变更后规范化（与 store 同名方法语义一致） */
  afterClipGeometryChange(layer: AnimationLayer): void
  /** 同步图层 / clip 选中态 */
  syncLayerSelection(): void
  /**
   * 从文档移除图层（可选撤销屏幕对象 URL）。
   *
   * @param layerId - 图层 id
   * @param revokeScreenTexture - 为 true 时 `URL.revokeObjectURL`
   */
  removeLayer(layerId: string, revokeScreenTexture: boolean): void
  /**
   * 在列表末尾（或指定下标）插入图层。
   *
   * @param layer - 图层副本
   * @param index - 插入位置；省略则 push
   */
  insertLayer(layer: AnimationLayer, index?: number): void
  /**
   * 用快照替换同 id 图层引用。
   *
   * @param layerId - 目标 id
   * @param snapshot - 替换用深拷贝
   */
  replaceLayer(layerId: string, snapshot: AnimationLayer): void
}

let activeBridge: MockupHistoryBridge | null = null

/**
 * 由 `mockupEditor` store 在 setup 内注册。
 *
 * @param bridge - 桥接实现；`null` 表示卸载（测试用）
 */
export function setMockupHistoryBridge(bridge: MockupHistoryBridge | null): void {
  activeBridge = bridge
}

/**
 * @returns 当前桥接
 * @throws 若尚未注册
 */
export function getMockupHistoryBridge(): MockupHistoryBridge {
  if (!activeBridge) {
    throw new Error('MockupHistoryBridge not initialized')
  }
  return activeBridge
}

/**
 * @returns 是否已注册桥接（测试 / 热更新容错）
 */
export function hasMockupHistoryBridge(): boolean {
  return activeBridge != null
}
